import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import './HelpVideos.css';

const HelpVideos = () => {
  // Helper function to safely trigger file input clicks
  const triggerFileInput = (selector) => {
    const input = document.querySelector(selector);
    if (input) {
      input.click();
    } else {
      console.warn(`File input with selector "${selector}" not found`);
    }
  };

  const [helpVideos, setHelpVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchHelpVideos();
  }, []);

  const fetchHelpVideos = async () => {
    try {
      setLoading(true);

      // Fetch all documents from helpVideos collection
      const querySnapshot = await getDocs(collection(db, 'helpVideos'));
      
      const videosList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Only include documents that have the required fields
        if (data.createdAt && data.url) {
          videosList.push({
            id: doc.id,
            createdAt: data.createdAt,
            description: data.description || '',
            link: data.link || '',
            name: data.name || '',
            size: data.size || 0,
            storagePath: data.storagePath || '',
            title: data.title || '',
            type: data.type || '',
            url: data.url || '',
            thumbnailUrl: data.thumbnailUrl || '',
            thumbnailStoragePath: data.thumbnailStoragePath || ''
          });
        }
      });
      
      // Sort by createdAt (newest first)
      videosList.sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate - aDate;
      });

      setHelpVideos(videosList);
    } catch (error) {
      console.error('Error fetching help videos:', error);
      // Set empty array on error to prevent UI issues
      setHelpVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      link: ''
    });
    setSelectedFile(null);
    setSelectedThumbnail(null);
    setFilePreview('');
    setThumbnailPreview('');
    setShowForm(true);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title || '',
      description: video.description || '',
      link: video.link || ''
    });
    setSelectedFile(null);
    setSelectedThumbnail(null);
    setFilePreview(video.url || '');
    setThumbnailPreview(video.thumbnailUrl || '');
    setShowForm(true);
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;

    try {
      await deleteDoc(doc(db, 'helpVideos', videoToDelete.id));
      await fetchHelpVideos();
      setShowDeleteConfirm(false);
      setVideoToDelete(null);
      
    } catch (error) {
      console.error('Error deleting help video:', error);
      alert('Error deleting help video. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setVideoToDelete(null);
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    
    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileName = `help-videos/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        name: file.name,
        size: file.size,
        type: file.type,
        storagePath: fileName
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      let fileData = null;
      
      // Upload new file if selected
      if (selectedFile) {
        fileData = await uploadFile(selectedFile);
      } else if (editingVideo && editingVideo.url) {
        // Keep existing file data for edit mode
        fileData = {
          url: editingVideo.url,
          name: editingVideo.name,
          size: editingVideo.size,
          type: editingVideo.type,
          storagePath: editingVideo.storagePath
        };
      }
      
      // Only save the exact data structure specified
      const videoData = {
        createdAt: editingVideo ? editingVideo.createdAt : new Date().toISOString(),
        description: formData.description,
        link: formData.link,
        title: formData.title,
        ...(fileData && {
          name: fileData.name,
          size: fileData.size,
          type: fileData.type,
          storagePath: fileData.storagePath,
          url: fileData.url
        })
      };

      if (editingVideo) {
        await updateDoc(doc(db, 'helpVideos', editingVideo.id), videoData);
        setHelpVideos(helpVideos.map(video => 
          video.id === editingVideo.id ? { ...video, ...videoData } : video
        ));
      } else {
        const docRef = await addDoc(collection(db, 'helpVideos'), videoData);
        setHelpVideos([{ id: docRef.id, ...videoData }, ...helpVideos]);
      }

      setShowForm(false);
      setFormData({ title: '', description: '', link: '' });
      setSelectedFile(null);
      setFilePreview('');
      setEditingVideo(null);
    } catch (error) {
      console.error('Error saving help video:', error);
      alert('Error saving help video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview('');
      }
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview('');
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview('');
  };

  if (loading) {
    return (
      <div className="help-videos-loading">
        <div className="loading-spinner"></div>
        <p>Loading help videos...</p>
      </div>
    );
  }

  return (
    <div className="help-videos-page">
      <div className="page-header help-videos-header">
        <div>
          <h2>Help Videos</h2>
        </div>
        <div className="help-videos-actions">
          <button onClick={handleAdd} className="btn btn-primary">
            + Add Help Video
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingVideo ? 'Edit Help Video' : 'Upload New Help Video'}</h3>
              <button 
                onClick={() => setShowForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="help-video-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter title"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Link</label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  placeholder="Enter related link"
                />
              </div>

              <div className="form-group">
                <label>Media File *</label>
                <div 
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {filePreview ? (
                    <div className="file-preview">
                      <img src={filePreview} alt="Preview" />
                      <button 
                        type="button" 
                        className="remove-file-btn"
                        onClick={removeFile}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">📁</div>
                      <p>Drag and drop a file here, or click to select</p>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileInput}
                        className="file-input"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => triggerFileInput('.file-input')}
                      >
                        Choose File
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Uploading...' : (editingVideo ? 'Update' : 'Upload')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="help-videos-grid">
        {helpVideos.length === 0 ? (
          <div className="no-videos">
            <div className="no-videos-icon">🎥</div>
            <h3>No Help Videos Found</h3>
            <p>Add some help videos to get started!</p>
            <button onClick={handleAdd} className="btn btn-primary">
              Add First Video
            </button>
          </div>
        ) : (
          helpVideos.map(video => {
            const isVideo = (video.type || '').startsWith('video/');
            const isImage = (video.type || '').startsWith('image/');
            
            return (
              <div key={video.id} className="video-card">
                <div className="video-thumbnail">
                  {isVideo ? (
                    <video src={video.url} controls={false} muted preload="metadata" />
                  ) : isImage ? (
                    <img src={video.url} alt={video.title || video.name} />
                  ) : (
                    <div className="file-icon">📄</div>
                  )}
                </div>
                
                <div className="video-info">
                  <h3>{video.title || video.name}</h3>
                  {video.description && <p className="description">{video.description}</p>}
                  {video.link && (
                    <p className="link">
                      <a href={video.link} target="_blank" rel="noreferrer">{video.link}</a>
                    </p>
                  )}
                  <div className="video-meta">
                    <span className="file-type">{video.type}</span>
                    <span className="file-size">{(video.size / 1024).toFixed(1)} KB</span>
                  </div>
                </div>
                
                <div className="video-actions">
                  <a href={video.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">
                    Open
                  </a>
                  <button 
                    onClick={() => handleEdit(video)}
                    className="btn btn-sm btn-secondary"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(video)}
                    className="btn btn-sm btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-modal-header">
              <h2>Confirm Delete</h2>
              <button 
                className="close-btn"
                onClick={handleDeleteCancel}
              >
                ✕
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="warning-icon">
                ⚠️
              </div>
              
              <div className="delete-message">
                <p>Are you sure you want to delete this help video? This will also delete all associated data.</p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="delete-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                Delete Video
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpVideos;
